import { Inject } from '@odinjs/odin';

import { ContextualBarAPI, FormAPI, InputAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import moment from 'moment';

import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { FragmentNames, NeonatalTpnTerminateAliases } from '../../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { Endpoint } from '../../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../../constants/routes';
import BaseController from '../../../../BaseController';
import MedicationService from '../../../services/medication-service';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';
import NeonatalTpnUtils from '../neonatal-tpn-utils';

interface GenerateChangePrescribedSolutionResponse {
  messageDescription: string;
}

type NeonatalTpnTerminateRequestBody = {
  changeType?: number;
  dateChange?: string,
  dateHour?: string,
  despisedVolumeQuantity?: number,
  encounterNumber?: number,
  infusedVolumeQuantity?: number,
  infusionSpeedQuantity?: number,
  justificationDescription?: string,
  partialVolumeQuantity?: number,
  prescriptionNumber?: number,
  solutionSequenceNumber?: number,
  solutionType?: number,
  volumePhaseQuantity?: number,
}

@Controller({ component: NeonatalTpnTerminateAliases.NEONATAL_TPN_TERMINATE_LAYOUT, fragment: FragmentNames.NEONATAL_TPN_TERMINATE })
export default class NeonatalTpnTerminateAliasesController extends BaseController {

  @Inject(NeonatalTpnTerminateAliases.NEONATAL_TPN_TERMINATE_FORM)
  private interruptFormAPI!: FormAPI;

  @Inject(NeonatalTpnTerminateAliases.PARTIAL_VOLUME_INPUT)
  private partialVolumeInputAPI!: InputAPI;

  @Inject(NeonatalTpnTerminateAliases.DISCARDED_VOLUME_INPUT)
  private discardedVolumeInputAPI!: InputAPI;

  @Inject(NeonatalTpnTerminateAliases.NOTES_TEXT_AREA)
  private notesJustificationTextAreaAPI!: InputAPI;

  @Inject(NeonatalTpnTerminateAliases.INFUSED_VOLUME_INPUT)
  private infusedVolumeInputAPI!: InputAPI;

  @Inject(NeonatalTpnTerminateAliases.STAGE_VOLUME_INPUT)
  private stageVolumeInputAPI!: InputAPI;

  @OnMount(NeonatalTpnTerminateAliases.CONTEXTUAL_BAR_FORM)
  handleMountContextualBarForm({ target: contextualBarApi }: EventData<ContextualBarAPI>): void {
    const materialDescription = MedicineAndSolutionsParams.params.dsMaterialId as string
    contextualBarApi.setTitle(materialDescription)
  }

  @OnMount(NeonatalTpnTerminateAliases.NEONATAL_TPN_TERMINATE_FORM)
  handleMountForm({ target: formApi }: EventData<FormAPI>): void {
    const { totalVolume } = this.historyService.getCurrentRouteParams();

    const defaultValue = 0
    formApi.setData({
      discardedVolume: defaultValue,
      infusedVolume: defaultValue,
      observation: '',
      partialVolume: defaultValue,
      stageVolume: totalVolume ?? defaultValue,
    })
  }

  @OnMount(NeonatalTpnTerminateAliases.DATE_INPUT)
  handleDateFieldMount({ target: inputApi }: EventData<InputAPI>): void {
    const currentTimeMoment = moment();
    this.context.set({ currentTime: currentTimeMoment.format(ISO_FULL_DATE_FORMAT) });
    inputApi.setValue(currentTimeMoment.utc().format(ISO_FULL_DATE_FORMAT));
  }

  @OnBeforeSubmit(NeonatalTpnTerminateAliases.NEONATAL_TPN_TERMINATE_FORM)
  handleBeforeSubmit(): void {
    const body = this.getRequestBody()
    this.handleTerminateEnteralNutritionRequest(body);
  }

  @OnBeforeCancel(NeonatalTpnTerminateAliases.NEONATAL_TPN_TERMINATE_FORM)
  onFormCancel(): void {
    applicationContext.popNavigationItem();
  }

  @OnChange(NeonatalTpnTerminateAliases.INFUSED_VOLUME_INPUT)
  handleInfusedVolumeInputChange(event: ChangeEvent<number>): void {
    this.handleVolumeInputChange(event, this.discardedVolumeInputAPI);
  }

  @OnChange(NeonatalTpnTerminateAliases.DISCARDED_VOLUME_INPUT)
  handleDiscardedVolumeInputChange(event: ChangeEvent<number>): void {
    this.handleVolumeInputChange(event, this.infusedVolumeInputAPI);
  }

  private getRequestBody(): NeonatalTpnTerminateRequestBody {
    const { encounterId, prescriptionNumber, startDate, sequenceNumber } = this.historyService.getCurrentRouteParams();
    const itemType = this.historyService.getCurrentRouteParams().itemType as string;
    const { currentTime } = this.context.getBulkData() as { currentTime: string };
    const changeTypeTerminate = 4;

    return {
      changeType: changeTypeTerminate,
      dateChange: currentTime,
      dateHour: moment(startDate as string).format(ISO_FULL_DATE_FORMAT),
      despisedVolumeQuantity: this.discardedVolumeInputAPI.getValue() as number,
      encounterNumber: encounterId as number,
      infusedVolumeQuantity: this.infusedVolumeInputAPI.getValue() as number,
      justificationDescription: this.notesJustificationTextAreaAPI.getValue() as string,
      partialVolumeQuantity: this.partialVolumeInputAPI.getValue() as number,
      prescriptionNumber: Number(prescriptionNumber),
      solutionSequenceNumber: Number(sequenceNumber),
      solutionType: NeonatalTpnUtils.getSolutionTypeByItemType(itemType),
      volumePhaseQuantity: this.infusedVolumeInputAPI.getValue() as number,
    }
  }

  private handleTerminateEnteralNutritionRequest(body: NeonatalTpnTerminateRequestBody): Promise<void> {
    this.interruptFormAPI.setLoading(true)
    this.interruptFormAPI.setEditing(false)
    return HttpService.post<GenerateChangePrescribedSolutionResponse>(Endpoint.ADEP_GENERATE_CHANGE_SOLUCAO_PRESCR, {}, { body })
      .then(({ body: responseBody }) => {
        if (responseBody) {
          this.showResponseMessage(responseBody?.messageDescription);
          this.redirectToTimeline();
        }
      }).catch(error => {
        if (error.body && error.body.errors) {
          error.body.errors.forEach((message: string) => MedicationService.showToast(message, ToastLevel.ERROR));
        }
      }).finally(() => {
        this.interruptFormAPI.setEditing(true)
        this.interruptFormAPI.setLoading(false)
      });
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    MedicationService.showToast(MessageService.translate('label.infusion-viewer.toast-infusion-terminated'), ToastLevel.SUCCESS);
  }

  private handleVolumeInputChange(event: ChangeEvent<number>, input: InputAPI): void {
    const actualValue = event.value as number || 0;
    const oldValue = event.oldValue;
    const totalVolume = Number(this.stageVolumeInputAPI.getValue());

    if (actualValue && (actualValue !== oldValue)) {
      const partialVolume = this.partialVolumeInputAPI.getValue() as number;
      const updatedVolume = this.calculateVolumeSolution(actualValue, partialVolume, totalVolume);
      input.setValue(updatedVolume);
    }
  }

  private calculateVolumeSolution(infusedVolume: number, partialVolume: number, stageVolume: number): number {
    const minimumValue = 0;
    if (infusedVolume !== minimumValue && stageVolume > minimumValue) {
      const result = stageVolume - partialVolume - infusedVolume;
      return result < minimumValue ? minimumValue : result;
    }

    return minimumValue;
  }
}